import { useState, useEffect } from 'react';
import * as Yup from "yup";
import { useCookies } from 'react-cookie';
import { Formik, Form } from "formik";
import { PropTypes } from 'prop-types';
import PDFMenuUploadField from './PDFMenuUploadField';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import { useMutation, useQueryCache } from 'react-query';
import { useNavigate, useLocation } from "react-router-dom";
import { usePlace } from '../api/queries';
import { uploadPdfMenu } from '../api/mutations';
import Loader from './Loader';
import PDFPreview from './PDFPreview';
import { trackEventSegment } from '../utils/segmentUtils';

const ALLOWED_FILE_TYPES = [
    'application/pdf',
]

function PDFSetUp(props) {
    const [cookies] = useCookies(["placeId"]);

    let navigate = useNavigate();
    let location = useLocation();

    let cache = useQueryCache();
    const [mutate, {status}] = useMutation(uploadPdfMenu);

    const [loaded, setLoaded] = useState(false);

    const [ formData, setFormData ] = useState({})

    const { data } = usePlace(cookies.placeId);

    const subHeaderExtra = location.pathname.includes("initial")
      ? "displayed if you don't create a visual menu in the next step"
      : "displayed instead of a visual menu";

    useEffect(() => {
        props.setHeader('Menu builder')
        props.setSubheader("Upload a PDF file of your menu which will be " + subHeaderExtra);
        return function cleanup() {
            props.setHeader('')
            props.setSubheader('')
        }
    })

    useEffect(() => {
        if (loaded === false && data) {
            setFormData({
                pdfUpload: data.menu_pdf
            })
            setLoaded(true);
        }
    }, [loaded, data])

    const onSubmit = async (values, bag) => {
        setLoaded(false);
        let reader = new FileReader();
        reader.onloadend = async () => {
            await mutate(
              {
                placeId: cookies.placeId,
                values: {
                  menu_pdf: reader.result,
                },
              },
              {
                onSuccess: (data) => {
                  if (location.pathname.includes("initial")) {
                    navigate("/menu-builder/visual-menu-choice");
                    trackEventSegment("Uploaded PDF Menu (Initial)", {
                      pdfUrl: data.data.menu_pdf,
                    });
                  } else {
                    trackEventSegment("Uploaded PDF Menu", {
                      pdfUrl: data.data.menu_pdf,
                    });
                  }
                },
              }
            );
            bag.setSubmitting(false);
            setFormData({pdfUpload: values.pdfUpload})
        }
        reader.readAsDataURL(values.pdfUpload)
    }

    const clearOption = () => {
        setFormData({});
        mutate(
          {
            placeId: cookies.placeId,
            values: {},
          },
          {
            onSuccess: (data) => {
              trackEventSegment("Cleared uploaded PDF");
            },
          }
        ).then(() => {
          cache.invalidateQueries("placeDetail");
        });
    }

    const schema = Yup.object({
        pdfUpload: Yup.mixed().test({
            name: 'fileFormat',
            test: function test(value) {
            if (!!value) {
                if (!ALLOWED_FILE_TYPES.includes(value.type)) {
                    return this.createError({
                        path: 'pdfUpload',
                        message: "File format '" + value.type + "' is not supported."
                    })
                }
            }
            return true;
        }}),
    })

    const handleLoading = () => {
        setLoaded(false);
    }

    const goNext = () => {
        trackEventSegment("Skipped uploading PDF");
        navigate(location.pathname.includes("initial") ? "/menu-builder/visual-menu" : "/finish");
    }

    const buttonGroup = () => {
      if (location.pathname.includes("initial") && data?.menu_pdf === null) {
        return (
          <ButtonGroup halfWidth={true}>
            <Button type="button" inversed={true} onClick={() => navigate(-1)} text="Back" />
            <Button type="button" text="Skip to visual menu" onClick={goNext} />
          </ButtonGroup>
        );
      }
      return (
        <ButtonGroup halfWidth={true}>
          <Button type="button" inversed={true} onClick={() => navigate("/menu-builder")} text="Back" />
          <Button type="button" text="Next" onClick={goNext} />
        </ButtonGroup>
      );
    };

    return (
      <Formik onSubmit={onSubmit} initialValues={formData} validationSchema={schema}>
        {(formik) =>
          status === "loading" ? (
            <Loader />
          ) : (
            <Form>
              {formData.pdfUpload && <PDFPreview url={formData.pdfUpload} clearOption={clearOption} />}
              <PDFMenuUploadField name="pdfUpload" text="text" helpText="help text" setLoading={handleLoading} />
              {buttonGroup()}
            </Form>
          )
        }
      </Formik>
    );
}


PDFSetUp.propTypes = {
    setHeader: PropTypes.func.isRequired,
    setSubheader: PropTypes.func.isRequired,
};

export default PDFSetUp;
